
import { Directive, ElementRef, HostListener } from "@angular/core";

@Directive({
    selector: "[sdInputMask]"
})
export class ShortDateInputMaskDirective {

    private oldLength: number;
    private oldValue: string;
    private caretPos: number;
    private readonly mask = "99/99/9999";
    private readonly slotChar = "";
    private readonly showPlaceholder = false;
    private readonly input: any;
    private value: string;
    private focus: boolean;
    private readonly overwriteOnInsert = false;
    private clear: boolean;
    private update: boolean;

    constructor(el: ElementRef) {
        this.input = el.nativeElement;
    }

    @HostListener("input")
    onInput() {
        if (this.update) {
            this.update = false;
            return;
        }
        this.checkValue();
    }


    private isNumeric(s: string) {
        if (s === " ") {
            return false;
        }
        return !isNaN(Number(s));
    }

    public checkValue(onFocus = false) {

        this.oldValue = this.value;
        this.value = this.input.value;
        this.focus = onFocus;

        if (!this.oldValue && !this.isInputKeyAcceptable()) {
            this.caretPos = this.input.selectionStart;
            this.value = this.oldValue;
            this.input.value = '';
            this.input.selectionStart = this.caretPos - 1;
            this.input.selectionEnd = this.caretPos - 1;
            return;
        }

        if (!this.focus && this.oldValue && this.oldValue.length <= this.input.value.length && !this.isInputKeyAcceptable()) {
            this.caretPos = this.input.selectionStart;
            this.value = this.oldValue;
            this.input.value = this.oldValue;
            this.input.selectionStart = this.caretPos - 1;
            this.input.selectionEnd = this.caretPos - 1;
            return;
        }
        if (this.overwriteOnInsert && this.input.selectionStart < this.input.value.length && !this.focus) {
            let selectionStart = this.input.selectionStart;
            if (!this.isNumeric(this.value.charAt(selectionStart))) {
                selectionStart++;
            }
            this.value = this.value.slice(0, selectionStart) + this.value.slice(selectionStart + 1);

        }
        if (!this.value && !this.focus) {
            return;
        }
        this.maskValue();
    }

    /**
     * Input key accepts based on the validation of the format: mm/dd/yyyy with a validation as below:
     *  mm <= 12
     *  dd <= 31
     *  yyyy < 2999
     * @returns boolean
     */
    private isInputKeyAcceptable(): boolean {
        const loc = this.input.selectionStart;
        if (this.isNumeric(this.input.value.charAt(this.input.selectionStart - 1))) {
            const n = this.input.value.charAt(this.input.selectionStart - 1);
            if (loc == 1 && n <= 1) {
                return true;
            } else if (loc == 2) {
                if ((this.input.value.charAt(0) === '0' && 0 < n && n <= 9) || (this.input.value.charAt(0) === '1' && n < 3)) {
                    return true;
                }
            }
            else if (loc == 3 && 0 <= n && n < 4) {
                return true;
            } else if (loc == 5) {
                if (this.input.value.charAt(3) === '3' && n > 1) {
                    return false;
                }
                return true;
            } else if (loc == 6 && 0 < n && n < 3) {
                return true;
            } else if (loc >= 8) {
                return true;
            }
        }

        return false;
    }

    private getConstChars(): string[] {
        const constChars = this.mask.replace(/9/g, "").split("");
        return constChars.filter((v, i, a) => a.indexOf(v) === i);
    }

    public maskValue() {
        let maskedValue = "";
        let dif = 0;
        let foundPlaceholder = false;
        this.value = this.removeMask(this.value);

        for (let i = 0; i < this.mask.length && this.value.length !== i; i++) {
            const maskChar = this.mask.charAt(i + dif);
            const valueChar = this.value.charAt(i);
            if (this.showPlaceholder && valueChar === this.slotChar) {
                if (foundPlaceholder) {
                    break;
                }
                foundPlaceholder = true;
            }
            if (!this.isNumeric(valueChar)
                && valueChar !== maskChar) {
                this.value = this.value.substring(0, i) + this.value.substring(i + 1);
                i--;
            } else if (maskChar === "9") {
                if (this.isNumeric(valueChar)) {
                    maskedValue += valueChar;
                } else {
                    this.value = this.value.substring(0, i) + this.value.substring(i + 1);
                    i--;
                }
            } else if (maskChar !== valueChar && maskedValue.charAt(i + dif) !== maskChar) {
                maskedValue += maskChar;
                dif++;
                i--;
            } else {
                maskedValue += maskChar;
            }
        }

        this.oldLength = maskedValue.length;
        if (this.showPlaceholder) {
            maskedValue = this.fillWithPlaceholder(maskedValue);
            this.oldValue = this.fillWithPlaceholder(this.oldValue, true);
        }

        this.caretPos = this.getUpdatedCaretPos(maskedValue);
        this.value = maskedValue;
        this.updateInput();
    }

    public removeMask(value: string): string {
        let finalValue = value;
        const constChars = this.getConstChars();
        const regexpSlotChar = new RegExp(this.slotChar, "g");
        finalValue = finalValue.replace(regexpSlotChar, "");
        for (let constCharsKey of constChars) {
            const regExp = new RegExp("\\" + constCharsKey, "g");
            finalValue = finalValue.replace(regExp, "");
        }

        return finalValue;
    }

    public updateInput() {
        this.input.value = this.value;
        if (this.focus && this.clear) {
            this.caretPos = 0;
            setTimeout(() => {
                this.input.selectionStart = this.caretPos;
                this.input.selectionEnd = this.caretPos;
            }, 0);
        } else {
            this.input.selectionStart = this.caretPos;
            this.input.selectionEnd = this.caretPos;
        }
    }

    private getUpdatedCaretPos(maskedValue: string) {
        let caretPos = this.getCaretPos();
        const startCaretPos = caretPos;
        if (caretPos === this.input.value.length || caretPos === this.oldLength) {
            caretPos = this.oldLength;
        } else {
            while (caretPos < this.value.length &&
                this.mask.charAt(caretPos) !== "9") {
                caretPos++;
            }
        }
        if (this.mask.charAt(caretPos - 1) !== "9" && caretPos === startCaretPos) {
            caretPos++;
        }
        return caretPos;
    }

    private fillWithPlaceholder(value: string, oldValue = false): string {
        if (value === undefined || value === null) {
            return value;
        }

        if (!oldValue && this.focus) {
            this.clear = !value || value.length === 0;
        }
        let mask = this.mask.replace(/9/g, this.slotChar);
        mask = mask.substring(value.length, mask.length);
        value = value + mask;
        return value;
    }

    public getCaretPos() {
        return this.input.selectionStart;
    }
}

